import type { Degree, Distance, Rectangle } from "@pdfking/math"
import { Max, Total } from "@pdfking/math"
import {
  HALF_TURN,
  QUARTER_TURN,
  ZERO_DEGREE,
  ZERO_DISTANCE,
  ZERO_MARGINS,
} from "@pdfking/math/constants"
import { NullQ } from "@pdfking/structs"
import type { Atom } from "@pdfking/tfunc"

import type { Container, Page } from "#internal"
import { Align, Direction, FlexEnum, Justify } from "#internal"

import type { FillMode } from "../types/filling"
import type { Layout } from "../types/layout"
import { PageLeaf } from "./PageLeaf"
import { QuarterTurn } from "./TurnMargins"

/** What a layout is resolved against: the room it is given, and how it is
 * asked to spend what its children leave over. */
export type ResolveOptions = {
  filling?: FillMode
  /** The box the whole layout sits in. Left out, the layout is its own box. */
  size?: Rectangle<Distance>
}

type Box<D> = Container<D> & Rectangle<Distance>

/** An axis left `undefined` is one the node has no opinion on: it takes
 * whatever room it is given. */
type Asked = { height: Distance | undefined; width: Distance | undefined }

type Gaps = Record<Direction, number>

const NO_GAPS: Gaps = { [Direction.Column]: 0, [Direction.Row]: 0 }

/** A node measured bottom-up: the size it asks for, how many gaps' worth of
 * its parent's leftover it takes along each direction, and how it settles once
 * its parent tells it its size. */
type Measured<N> = { asked: Asked; gaps: Gaps; resolve: (size: Rectangle<Distance>) => N }

/** What the tree carries down: the filling a container spends with unless it
 * names its own, the guid prefix, and the directions an ancestor spends along. */
type Context = { filling: FillMode; prefix: Atom[]; spent: ReadonlySet<Direction> }

const main_of = <T>(size: { height: T; width: T }, direction: Direction): T =>
  direction === Direction.Row ? size.width : size.height

const cross_of = <T>(size: { height: T; width: T }, direction: Direction): T =>
  direction === Direction.Row ? size.height : size.width

const sized = <T>(main: T, cross: T, direction: Direction): { height: T; width: T } =>
  direction === Direction.Row ? { height: cross, width: main } : { height: main, width: cross }

// Compact packs its lines into the bottom-left corner of the sheet, so a
// shorter line (the turned strip) shares that edge and its far side is one
// straight cut. Seen from behind the corner is bottom-right, and a turned fill
// carries it round. The tree is y-down: the bottom is the end of a row's cross axis.
const corner = (is_front: boolean, rotation: Degree, direction: Direction): Align => {
  let [left, bottom] = [is_front, true]
  for (let i = 0; i < rotation / QUARTER_TURN; i++) [left, bottom] = [!bottom, left]
  return (direction === Direction.Column ? left : !bottom) ? Align.FlexStart : Align.FlexEnd
}

/**
 * A container of `children`, asking for `size` or, left out, for its children
 * summed along its direction and the widest of them across.
 *
 * What its box leaves over along the direction is shared a gap at a time: the
 * container keeps one per gap its `justify` opens, each child takes one per gap
 * it opens itself, and one that asked for no length takes one. Across, a
 * stretching container gives every child the whole of it.
 */
const stack = <D>(
  node: Omit<Container<D>, "children" | "height" | "width">,
  children: Measured<Container<D> | Page<D>>[],
  size?: Rectangle<Distance>
): Measured<Box<D>> => {
  const { direction, padding } = node
  const spacing =
    node.justify === Justify.SpaceBetween
      ? children.length - 1
      : node.justify === Justify.SpaceEvenly
        ? children.length + 1
        : 0
  const mains = children.map(child => main_of(child.asked, direction))
  const crosses = children.map(child => cross_of(child.asked, direction))
  const known = crosses.filter(cross => !NullQ(cross))
  const shares = children.map((child, i) => (NullQ(mains[i]) ? 1 : child.gaps[direction]))
  const opened = (along: Direction): number =>
    along === direction
      ? Total(spacing, ...children.map(child => child.gaps[along]))
      : Max(0, ...children.map(child => child.gaps[along]))
  return {
    asked:
      size ??
      sized(
        mains.some(main => NullQ(main)) ? undefined : Total(...(mains as Distance[])),
        known.length ? Max(...known) : undefined,
        direction
      ),
    gaps: { [Direction.Column]: opened(Direction.Column), [Direction.Row]: opened(Direction.Row) },
    resolve: box => {
      const inner = {
        height: (box.height - padding.top - padding.bottom) as Distance,
        width: (box.width - padding.left - padding.right) as Distance,
      }
      const cross = cross_of(inner, direction)
      const left = main_of(inner, direction) - Total(...mains.map(main => main ?? ZERO_DISTANCE))
      const gap = Max(0, left) / (Total(spacing, ...shares) || 1)
      return {
        ...node,
        children: children.map((child, i) =>
          child.resolve(
            sized(
              ((mains[i] ?? ZERO_DISTANCE) + shares[i]! * gap) as Distance,
              node.align === Align.Stretch ? cross : (crosses[i] ?? cross),
              direction
            )
          )
        ),
        height: box.height,
        width: box.width,
      }
    },
  }
}

const measure_page = <D>(
  layout: Extract<Layout<D>, { enum: "page" }>,
  { prefix }: Context
): Measured<Page<D>> => {
  // The cell is the subject plus its padding, on its side when turned.
  const { size, ...leaf } = PageLeaf(layout, prefix)
  const { padding } = leaf
  const height = size && ((size.height + padding.top + padding.bottom) as Distance)
  const width = size && ((size.width + padding.left + padding.right) as Distance)
  const asked = leaf.rotation % HALF_TURN ? { height: width, width: height } : { height, width }
  return {
    asked,
    gaps: NO_GAPS,
    // A page never grows past what it asked: that would print the subject
    // bigger than it is. Only one without a size takes the room it is given.
    resolve: room => ({
      ...leaf,
      enum: FlexEnum.Page,
      height: asked.height ?? room.height,
      width: asked.width ?? room.width,
    }),
  }
}

const measure_flex = <D>(
  layout: Extract<Layout<D>, { enum: "flex" }>,
  context: Context
): Measured<Box<D>> => {
  const filling = layout.fill_mode ?? context.filling
  const direction = layout.direction === "column" ? Direction.Column : Direction.Row
  const layouts = layout.layouts ?? []
  const is_front = layout.is_front ?? true
  const rotation = QuarterTurn(layout.rotation ?? ZERO_DEGREE)
  // Compact and center keep their children packed: nobody takes the leftover.
  const packed = filling === "compact" || filling === "center"
  // The first container spending along a direction spends the way the filling
  // says; one nested inside it along the same direction is handed exactly its
  // gaps' worth, and spends it between its children.
  const spends = !packed && layouts.length > 1
  const children = layouts
    .map(child =>
      measure(child, {
        filling,
        prefix: NullQ(layout.page_prefix)
          ? context.prefix
          : [...context.prefix, layout.page_prefix],
        spent: spends ? new Set([...context.spent, direction]) : context.spent,
      })
    )
    .map(child => (packed ? { ...child, gaps: NO_GAPS } : child))
  const node: Omit<Container<D>, "children" | "height" | "width"> = {
    align: packed ? Align.Center : Align.Stretch,
    data: layout.data,
    direction,
    enum: FlexEnum.Container,
    is_front,
    justify: !spends
      ? Justify.Center
      : filling === "distribute" || context.spent.has(direction)
        ? Justify.SpaceBetween
        : Justify.SpaceEvenly,
    padding: layout.padding ?? ZERO_MARGINS,
    rotation,
  }
  // A sized compact container has leftover of its own: it centres its
  // children in it as one block, packed into the corner.
  if (layout.size && filling === "compact") {
    const block = { ...node, align: corner(is_front, rotation, direction), data: undefined }
    return stack(node, [stack({ ...block, padding: ZERO_MARGINS }, children)], layout.size)
  }
  return stack(node, children, layout.size)
}

const measure = <D>(layout: Layout<D>, context: Context): Measured<Container<D> | Page<D>> =>
  layout.enum === "page" ? measure_page(layout, context) : measure_flex(layout, context)

/**
 * Walks a {@link Layout} tree and settles it: every padding folded into the
 * box that carries it, every size named, and the filling read as the flex a
 * stack spends its leftover with — the tree the layout pass then lays out.
 *
 * A bare page is a layout of one: it is wrapped in a stack of its own, so it
 * takes `size` the way any other only child of the root would.
 */
export const ResolveLayout = <D>(
  layout: Layout<D>,
  { filling = "compact", size }: ResolveOptions = {}
): Box<D> => {
  const root = measure_flex<D>(
    layout.enum === "page" ? { enum: "flex", fill_mode: filling, layouts: [layout] } : layout,
    { filling, prefix: [], spent: new Set() }
  )
  return root.resolve(
    size ?? { height: root.asked.height ?? ZERO_DISTANCE, width: root.asked.width ?? ZERO_DISTANCE }
  )
}
